import React, { useState, useMemo, useRef } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Box,
    Typography,
    Avatar,
    Button,
    CircularProgress,
    Card,
    CardContent,
    TextField,
    Tooltip,
    Paper,
    Divider,
    Chip,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    Settings as SettingsIcon,
    SmartToy as BotIcon,
    Save as SaveIcon,
    Person as PersonIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    Download as DownloadIcon,
    AutoAwesome as AutoAwesomeIcon,
    ChevronLeft as BackIcon,
    PostAdd as PostAddIcon,
    TipsAndUpdates as TipsAndUpdatesIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import RenderLatex from 'components/content-generation/RenderLatex.jsx';
import RichTextEditor from 'components/richTextEditor/richTextEditor';

// ContentGenerationAppBar Component
export const ContentGenerationAppBar = ({
    setDrawerOpen,
    domain,
    department,
    messages,
    isSaved,
    setIsSaved,
    isDownloading,
    isSaving,
    handleSave,
    handleDownload,
    isMobile,
    title = "Document Creator",
    onTitleChange
}) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState(title);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);

    // Only bot messages with real document content
    const botMessages = useMemo(() => {
        return messages
            .map((m, idx) => ({ ...m, chatIndex: idx }))
            .filter(m => m.type === 'bot' && m.content && m.content.trim());
    }, [messages]);

    // Track which message indices are selected for export
    const [selectedChatNos, setSelectedChatNos] = useState([]);

    const handleOpenExport = () => {
        // Default: pre-select the latest bot message
        const botIndices = botMessages.map(m => m.chatIndex);
        setSelectedChatNos(botIndices.length > 0 ? [botIndices[botIndices.length - 1]] : []);
        setExportDialogOpen(true);
    };

    const handleToggleChatNo = (chatIndex) => {
        setSelectedChatNos(prev =>
            prev.includes(chatIndex)
                ? prev.filter(n => n !== chatIndex)
                : [...prev, chatIndex]
        );
    };

    const handleSelectAll = () => {
        setSelectedChatNos(botMessages.map(m => m.chatIndex));
    };

    const handleConfirmExport = () => {
        setExportDialogOpen(false);
        // Map absolute message indices → sequential chat[] positions (bot-only)
        const botOnlyIndices = botMessages.map(m => m.chatIndex);
        const chatNos = selectedChatNos
            .filter(n => botOnlyIndices.includes(n))
            .map(n => botOnlyIndices.indexOf(n))
            .sort((a, b) => a - b);
        handleDownload(chatNos);
    };

    const handleTitleEdit = () => {
        setIsEditingTitle(true);
        setEditedTitle(title);
    };

    const handleTitleSave = () => {
        if (onTitleChange) {
            onTitleChange(editedTitle);
        }
        setIsEditingTitle(false);
        setIsSaved(false);
    };

    const handleTitleCancel = () => {
        setEditedTitle(title);
        setIsEditingTitle(false);
    };

    const handleTitleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleTitleSave();
        } else if (e.key === 'Escape') {
            handleTitleCancel();
        }
    };

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={{
                bgcolor: 'white',
                borderBottom: '1px solid #e0e0e0',
                color: 'text.primary',
                zIndex: 1100,
                flexShrink: 0
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        edge="start"
                        onClick={() => setDrawerOpen(true)}
                        sx={{ mr: 2, color: 'primary.main', bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}
                    >
                        <SettingsIcon />
                    </IconButton>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {isEditingTitle ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    size="small"
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    onKeyDown={handleTitleKeyPress}
                                    autoFocus
                                    sx={{ '& .MuiOutlinedInput-input': { fontWeight: 'bold', py: 0.5 } }}
                                />
                                <IconButton size="small" onClick={handleTitleSave} color="primary">
                                    <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleTitleCancel}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#101828', letterSpacing: '-0.02em' }}>
                                    {title}
                                </Typography>
                                <IconButton size="small" onClick={handleTitleEdit} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                                    <EditIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        )}
                        {domain && department && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
                                {domain} • {department}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    {messages.length > 0 && (
                        <Chip 
                            label={isSaved ? "Saved" : "Unsaved Changes"} 
                            size="small"
                            color={isSaved ? "success" : "warning"}
                            variant="outlined"
                            sx={{ fontWeight: 600, px: 0.5 }}
                        />
                    )}
                    
                    <Tooltip
                        title={!isSaved ? "Save the document first to export PDF" : ""}
                        arrow
                        disableHoverListener={isSaved}
                        disableFocusListener={isSaved}
                        disableTouchListener={isSaved}
                    >
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                                onClick={handleOpenExport}
                                disabled={botMessages.length === 0 || isDownloading || !isSaved}
                                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                            >
                                Export PDF
                            </Button>
                        </span>
                    </Tooltip>

                    <Button
                        variant="contained"
                        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={messages.length === 0 || isSaving || (isSaved && messages.length > 0)}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, boxShadow: 'none' }}
                    >
                        Save
                    </Button>
                </Box>
            </Toolbar>

            {/* ── Version Selection Dialog ── */}
            <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.1rem', pb: 0.5 }}>
                    Export to PDF
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                        Select which generated document version(s) to include in the PDF.
                        Each version reflects a generation or feedback revision.
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                        <Button size="small" onClick={handleSelectAll} sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#23479F' }}>
                            Select All ({botMessages.length})
                        </Button>
                    </Box>

                    <List dense disablePadding sx={{ border: '1px solid #e8ecf1', borderRadius: 2, overflow: 'hidden' }}>
                        {botMessages.map((msg, i) => {
                            const isSelected = selectedChatNos.includes(msg.chatIndex);
                            const preview = msg.content.replace(/<[^>]+>/g, '').slice(0, 80).trim();
                            return (
                                <ListItem
                                    key={msg.chatIndex}
                                    button
                                    onClick={() => handleToggleChatNo(msg.chatIndex)}
                                    divider={i < botMessages.length - 1}
                                    sx={{
                                        bgcolor: isSelected ? '#f0f4ff' : 'white',
                                        transition: 'background 0.15s',
                                        '&:hover': { bgcolor: '#f0f4ff' },
                                        py: 1.2, px: 2
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Checkbox
                                            edge="start"
                                            checked={isSelected}
                                            size="small"
                                            sx={{ color: '#23479F' }}
                                            onClick={e => e.stopPropagation()}
                                            onChange={() => handleToggleChatNo(msg.chatIndex)}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={`Version ${i + 1}`}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700, fontSize: '0.7rem', height: 20,
                                                        bgcolor: isSelected ? '#23479F' : '#e8ecf1',
                                                        color: isSelected ? 'white' : '#475569',
                                                    }}
                                                />
                                                {(messages[msg.chatIndex - 1]?.content || '').startsWith('Feedback:') ? (
                                                    <Chip label="Revised" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', height: 18 }} />
                                                ) : null}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.3 }}>
                                                {preview}{msg.content.replace(/<[^>]+>/g, '').length > 80 ? '…' : ''}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setExportDialogOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmExport}
                        disabled={selectedChatNos.length === 0 || isDownloading}
                        startIcon={isDownloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, borderRadius: 2,
                            background: 'linear-gradient(135deg, #23479F 0%, #3b63c7 100%)',
                            boxShadow: '0 4px 12px rgba(35,71,159,0.25)'
                        }}
                    >
                        Export {selectedChatNos.length} Version{selectedChatNos.length !== 1 ? 's' : ''}
                    </Button>
                </DialogActions>
            </Dialog>
        </AppBar>
    );
};

// ContentGenerationMessages Component
export const ContentGenerationMessages = ({
    messages,
    isGenerating,
    currentResponse,
    messagesEndRef,
    onMessageEdit,
    onMessageCopy,
    onMessageDelete,
    setIsSaved,
    isSaved,
    onSectionImageRequest,
}) => {
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const editableRef = useRef(null);

    const handleEditStart = (message) => {
        setEditingMessageId(message.id);
    };

    const handleEditCancel = () => {
        setEditingMessageId(null);
    };

    const handleEditSave = (messageId) => {
        let newContent = "";
        if (editableRef.current) {
            // Find the child div rendered by RenderLatex inside our editable wrapper
            const innerDiv = editableRef.current.querySelector('div');
            newContent = innerDiv ? innerDiv.innerHTML : editableRef.current.innerHTML;
        }

        if (onMessageEdit) {
            onMessageEdit(messageId, newContent);
        }
        setEditingMessageId(null);
        if (setIsSaved) {
            setIsSaved(false);
        }
    };

    const handleCopy = (content, messageId) => {
        // Strip out HTML tags to construct a clean plain text layout for fallback/plain pasting
        let cleanText = content
            .replace(/<h5[^>]*>(.*?)<\/h5>/gi, '\n\n$1\n\n')
            .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
            .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
            .replace(/<br\s*\/?>/gi, '\n');

        // Parse HTML entities using browser DOMParser
        const doc = new DOMParser().parseFromString(cleanText, 'text/html');
        const formattedText = (doc.body.textContent || doc.body.innerText || "")
            .replace(/\n{3,}/g, '\n\n') // Normalize multiple line breaks
            .trim();

        const fallbackCopy = () => {
            navigator.clipboard.writeText(formattedText).then(() => {
                if (onMessageCopy) {
                    onMessageCopy(content);
                }
                setCopiedId(messageId);
                setTimeout(() => setCopiedId(null), 2000);
            });
        };

        // If browser supports modern ClipboardItem, write both Rich HTML and plain text
        if (typeof ClipboardItem !== 'undefined') {
            try {
                const htmlBlob = new Blob([content], { type: 'text/html' });
                const textBlob = new Blob([formattedText], { type: 'text/plain' });

                navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': htmlBlob,
                        'text/plain': textBlob
                    })
                ]).then(() => {
                    if (onMessageCopy) {
                        onMessageCopy(content);
                    }
                    setCopiedId(messageId);
                    setTimeout(() => setCopiedId(null), 2000);
                }).catch(err => {
                    console.warn('Rich text copy failed, falling back to plain text', err);
                    fallbackCopy();
                });
            } catch (err) {
                console.warn('Rich text copy initialization failed, falling back to plain text', err);
                fallbackCopy();
            }
        } else {
            fallbackCopy();
        }
    };

    return (
        <Box sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: { xs: 2, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            bgcolor: '#f9fafb'
        }}>
            {messages.length === 0 && !isGenerating && (
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 3,
                    py: 6,
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    {/* Subtle decorative blobs */}
                    <Box sx={{
                        position: 'absolute', top: -60, right: -80, width: 300, height: 300,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)',
                        pointerEvents: 'none'
                    }} />
                    <Box sx={{
                        position: 'absolute', bottom: -40, left: -60, width: 250, height: 250,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)',
                        pointerEvents: 'none'
                    }} />

                    {/* Pulsing icon */}
                    <Box sx={{ position: 'relative', mb: 4 }}>
                        <Box sx={{
                            position: 'absolute', inset: -12,
                            borderRadius: '50%',
                            border: '2px solid rgba(99,102,241,0.18)',
                            animation: 'pulse-ring 2.4s ease-out infinite',
                            '@keyframes pulse-ring': {
                                '0%': { transform: 'scale(0.85)', opacity: 1 },
                                '100%': { transform: 'scale(1.25)', opacity: 0 }
                            }
                        }} />
                        <Box sx={{
                            width: 80, height: 80, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 12px 32px rgba(99,102,241,0.32)'
                        }}>
                            <PostAddIcon sx={{ fontSize: 38, color: 'white' }} />
                        </Box>
                    </Box>

                    {/* Headline */}
                    <Typography variant="h4" sx={{
                        fontWeight: 900,
                        letterSpacing: '-0.03em',
                        mb: 1.5,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #1e293b 0%, #6366f1 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        Enterprise Document Creator
                    </Typography>

                    <Typography variant="body1" sx={{
                        color: '#64748b', textAlign: 'center',
                        maxWidth: 440, lineHeight: 1.7, mb: 5,
                        fontSize: '1rem'
                    }}>
                        Generate structured, professional training content tailored to your domain and audience.
                    </Typography>

                    {/* Steps row */}
                    <Box sx={{
                        display: 'flex', gap: 2, width: '100%', maxWidth: 580,
                        mb: 5, flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        {[
                            {
                                num: '01',
                                color: '#6366f1',
                                bg: '#eef2ff',
                                border: '#c7d2fe',
                                title: 'Configure',
                                desc: 'Select a pre-uploaded resource file from the sidebar, or manually configure domain, department & audience level.'
                            },
                            {
                                num: '02',
                                color: '#0ea5e9',
                                bg: '#f0f9ff',
                                border: '#bae6fd',
                                title: 'Describe',
                                desc: 'Enter your topic in the input below and hit Generate.'
                            },
                            {
                                num: '03',
                                color: '#10b981',
                                bg: '#f0fdf4',
                                border: '#bbf7d0',
                                title: 'Review',
                                desc: 'Read, edit, export as PDF, or regenerate the document.'
                            }
                        ].map(step => (
                            <Box key={step.num} sx={{
                                flex: 1, p: 2.5,
                                bgcolor: step.bg,
                                border: `1px solid ${step.border}`,
                                borderRadius: 3,
                                display: 'flex', flexDirection: 'column', gap: 0.5
                            }}>
                                <Typography sx={{
                                    fontSize: '1.4rem', fontWeight: 900,
                                    color: step.color, lineHeight: 1,
                                    fontVariantNumeric: 'tabular-nums'
                                }}>{step.num}</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                    {step.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.5, fontSize: '0.78rem' }}>
                                    {step.desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                </Box>
            )}

            {messages.map((message) => (
                <Box 
                    key={message.id} 
                    sx={{ 
                        maxWidth: 900, 
                        width: '100%',
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: message.type === 'user' ? 'flex-end' : 'flex-start'
                    }}
                >
                    {message.type === 'user' ? (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'flex-end', 
                            gap: 0.5, 
                            mb: 1.5,
                            maxWidth: '75%',
                            alignSelf: 'flex-end'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                                    You
                                </Typography>
                                <Avatar sx={{ width: 20, height: 20, bgcolor: '#e0e7ff', color: '#23479F', fontSize: '0.75rem', fontWeight: 700 }}>
                                    U
                                </Avatar>
                            </Box>
                            <Box sx={{
                                p: '12px 18px',
                                borderRadius: '18px 18px 2px 18px',
                                background: 'linear-gradient(135deg, #23479F 0%, #3b63c7 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(35,71,159,0.12)',
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                                    {message.content}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem', mt: 0.25 }}>
                                {message.timestamp ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                <Avatar sx={{ width: 20, height: 20, bgcolor: '#eef2ff', color: '#6366f1', fontSize: '0.75rem', fontWeight: 700 }}>
                                    AI
                                </Avatar>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                                    Vijnata AI
                                </Typography>
                            </Box>
                            <Card elevation={0} sx={{
                                border: '1px solid #dde3ec',
                                borderRadius: 3,
                                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                                overflow: 'hidden',
                                position: 'relative',
                                bgcolor: 'white',
                                width: '100%'
                            }}>
                            {/* Document Header Bar */}
                            <Box sx={{
                                px: { xs: 3, md: 5 },
                                py: 1.5,
                                background: 'linear-gradient(135deg, #f8f9fb 0%, #f1f4f9 100%)',
                                borderBottom: '1px solid #e8ecf1',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5
                            }}>
                                <Box sx={{
                                    width: 28, height: 28, borderRadius: '6px',
                                    bgcolor: '#eef2ff', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <PostAddIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b', display: 'block', lineHeight: 1.2 }}>
                                        Generated Document
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem' }}>
                                        {new Date(message.timestamp).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title={copiedId === message.id ? "Copied!" : "Copy Content"}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(message.content, message.id)}
                                            sx={{
                                                color: copiedId === message.id ? '#10b981' : '#94a3b8',
                                                '&:hover': { color: copiedId === message.id ? '#10b981' : '#6366f1', bgcolor: '#eef2ff' }
                                            }}
                                        >
                                            {copiedId === message.id ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Document">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditStart(message)}
                                            sx={{ color: '#94a3b8', '&:hover': { color: '#6366f1', bgcolor: '#eef2ff' } }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <CardContent sx={{ p: { xs: 3, md: '40px 56px' } }}>
                                {editingMessageId === message.id && (
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 0.5,
                                        mb: 2.5,
                                        p: 0.75,
                                        bgcolor: '#f8fafc',
                                        borderRadius: 2,
                                        alignItems: 'center',
                                        border: '1px solid #e2e8f0',
                                        width: 'fit-content'
                                    }}>
                                        <Tooltip title="Bold (Ctrl+B)">
                                            <Button
                                                size="small"
                                                onClick={() => document.execCommand('bold', false)}
                                                sx={{ minWidth: 36, height: 32, p: 0, color: '#475569', fontWeight: 900, '&:hover': { bgcolor: '#f1f5f9' }, textTransform: 'none' }}
                                            >
                                                B
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Italic (Ctrl+I)">
                                            <Button
                                                size="small"
                                                onClick={() => document.execCommand('italic', false)}
                                                sx={{ minWidth: 36, height: 32, p: 0, color: '#475569', fontStyle: 'italic', '&:hover': { bgcolor: '#f1f5f9' }, textTransform: 'none' }}
                                            >
                                                I
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Bullet List">
                                            <Button
                                                size="small"
                                                onClick={() => document.execCommand('insertUnorderedList', false)}
                                                sx={{ minWidth: 36, height: 32, p: 0, color: '#475569', fontSize: '1.1rem', '&:hover': { bgcolor: '#f1f5f9' }, textTransform: 'none' }}
                                            >
                                                •
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Numbered List">
                                            <Button
                                                size="small"
                                                onClick={() => document.execCommand('insertOrderedList', false)}
                                                sx={{ minWidth: 36, height: 32, p: 0, color: '#475569', fontSize: '0.85rem', '&:hover': { bgcolor: '#f1f5f9' }, textTransform: 'none' }}
                                            >
                                                1.
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Heading">
                                            <Button
                                                size="small"
                                                onClick={() => document.execCommand('formatBlock', false, '<h5>')}
                                                sx={{ minWidth: 36, height: 32, p: 0, color: '#475569', fontWeight: 700, fontSize: '0.8rem', '&:hover': { bgcolor: '#f1f5f9' }, textTransform: 'none' }}
                                            >
                                                H
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                )}

                                <Box
                                    ref={editingMessageId === message.id ? editableRef : null}
                                    contentEditable={editingMessageId === message.id}
                                    suppressContentEditableWarning
                                    sx={{
                                        outline: editingMessageId === message.id ? '2px solid #23479F' : 'none',
                                        borderRadius: 2,
                                        p: editingMessageId === message.id ? 2 : 0,
                                        bgcolor: editingMessageId === message.id ? '#fafbfc' : 'transparent',
                                        transition: 'all 0.2s ease',
                                        '& h1': { fontSize: '1.9rem', fontWeight: 800, color: '#0f172a', mt: 0, mb: 2, letterSpacing: '-0.02em', lineHeight: 1.2 },
                                        '& h2': { fontSize: '1.3rem', fontWeight: 700, color: '#1e293b', mt: 4, mb: 1.5, pt: 3, borderTop: '1px solid #f1f5f9', letterSpacing: '-0.01em' },
                                        '& h3': { fontSize: '1.05rem', fontWeight: 700, color: '#334155', mt: 3, mb: 1 },
                                        '& h4, & h5, & h6': { fontSize: '0.95rem', fontWeight: 700, color: '#475569', mt: 2.5, mb: 1 },
                                        '& p': { mb: 1.8, lineHeight: 1.85, color: '#374151', fontSize: '0.925rem' },
                                        '& ul, & ol': { mb: 2, pl: 3.5 },
                                        '& li': { mb: 0.8, color: '#374151', lineHeight: 1.7, fontSize: '0.925rem' },
                                        '& b, & strong': { color: '#1e293b', fontWeight: 700 },
                                        '& em': { color: '#6366f1' },
                                        '& blockquote': { borderLeft: '3px solid #6366f1', pl: 2, ml: 0, my: 2, color: '#64748b', fontStyle: 'italic' },
                                        '& hr': { border: 'none', borderTop: '1px solid #e2e8f0', my: 3 },
                                        '& code': { bgcolor: '#f1f5f9', px: 0.7, py: 0.2, borderRadius: 1, fontSize: '0.8rem', color: '#6366f1' },
                                        '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                                        '& th': { bgcolor: '#f8fafc', fontWeight: 700, color: '#1e293b', p: 1, borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontSize: '0.8rem' },
                                        '& td': { p: 1, borderBottom: '1px solid #f1f5f9', color: '#374151', fontSize: '0.875rem', verticalAlign: 'top' }
                                    }}
                                >
                                    <RenderLatex content={message.content} onImageRequest={(section) => onSectionImageRequest && onSectionImageRequest(section, message.id)} />
                                </Box>

                                {editingMessageId === message.id && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
                                        <Button
                                            size="small"
                                            onClick={handleEditCancel}
                                            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleEditSave(message.id)}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                bgcolor: '#23479F',
                                                color: 'white',
                                                '&:hover': { bgcolor: '#1b3780' }
                                            }}
                                        >
                                            Save Changes
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                        </Box>
                    )}
                </Box>
            ))}


            {isGenerating && (
                <Box sx={{ maxWidth: 900, width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Avatar sx={{ width: 20, height: 20, bgcolor: '#eef2ff', color: '#6366f1', fontSize: '0.75rem', fontWeight: 700 }}>
                            AI
                        </Avatar>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                            Vijnata AI
                        </Typography>
                    </Box>
                    <Card elevation={0} sx={{
                        border: '1px solid #dde3ec',
                        borderRadius: 3,
                        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                        bgcolor: 'white',
                        width: '100%'
                    }}>
                        {/* Streaming Document Header Bar */}
                        <Box sx={{
                            px: { xs: 3, md: 5 },
                            py: 1.5,
                            background: 'linear-gradient(135deg, #f8f9fb 0%, #f1f4f9 100%)',
                            borderBottom: '1px solid #e8ecf1',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                        }}>
                            <Box sx={{
                                width: 28, height: 28, borderRadius: '6px',
                                bgcolor: '#eef2ff', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                                <PostAddIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b', display: 'block', lineHeight: 1.2 }}>
                                    Generated Document
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.3 }}>
                                    {/* Live writing indicator */}
                                    <Box sx={{
                                        width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366f1',
                                        animation: 'streamPulse 1.2s ease-in-out infinite',
                                        '@keyframes streamPulse': {
                                            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                            '50%': { opacity: 0.4, transform: 'scale(0.7)' }
                                        }
                                    }} />
                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem' }}>
                                        Writing...
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <CardContent sx={{ p: { xs: 3, md: '40px 56px' } }}>
                            <Box sx={{
                                '& h1': { fontSize: '1.9rem', fontWeight: 800, color: '#0f172a', mt: 0, mb: 2, letterSpacing: '-0.02em', lineHeight: 1.2 },
                                '& h2': { fontSize: '1.3rem', fontWeight: 700, color: '#1e293b', mt: 4, mb: 1.5, pt: 3, borderTop: '1px solid #f1f5f9', letterSpacing: '-0.01em' },
                                '& h3': { fontSize: '1.05rem', fontWeight: 700, color: '#334155', mt: 3, mb: 1 },
                                '& h4, & h5, & h6': { fontSize: '0.95rem', fontWeight: 700, color: '#475569', mt: 2.5, mb: 1 },
                                '& p': { mb: 1.8, lineHeight: 1.85, color: '#374151', fontSize: '0.925rem' },
                                '& ul, & ol': { mb: 2, pl: 3.5 },
                                '& li': { mb: 0.8, color: '#374151', lineHeight: 1.7, fontSize: '0.925rem' },
                                '& b, & strong': { color: '#1e293b', fontWeight: 700 },
                                '& em': { color: '#6366f1' },
                                '& blockquote': { borderLeft: '3px solid #6366f1', pl: 2, ml: 0, my: 2, color: '#64748b', fontStyle: 'italic' },
                                '& hr': { border: 'none', borderTop: '1px solid #e2e8f0', my: 3 },
                                '& code': { bgcolor: '#f1f5f9', px: 0.7, py: 0.2, borderRadius: 1, fontSize: '0.8rem', color: '#6366f1' },
                                '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                                '& th': { bgcolor: '#f8fafc', fontWeight: 700, color: '#1e293b', p: 1, borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontSize: '0.8rem' },
                                '& td': { p: 1, borderBottom: '1px solid #f1f5f9', color: '#374151', fontSize: '0.875rem', verticalAlign: 'top' }
                            }}>
                                <RenderLatex content={currentResponse} />
                                {/* Blinking cursor at end of stream */}
                                <Box component="span" sx={{
                                    display: 'inline-block', width: 2, height: '1em',
                                    bgcolor: '#6366f1', ml: 0.5, verticalAlign: 'text-bottom',
                                    animation: 'cursorBlink 1s step-end infinite',
                                    '@keyframes cursorBlink': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0 }
                                    }
                                }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            )}

            <div ref={messagesEndRef} style={{ height: 20 }} />
        </Box>
    );
};
